<template>
  <div v-if="data" class="relative h-full pb-4 overflow-x-auto bg-white">
    <table v-if="data" class="w-full text-sm text-left text-gray-900 divide-y divide-gray-200">
      <tbody>
        <!-- Status Information -->
        <BaseDetailsTableRow
          :name="$t('representative.isApproved')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseCheckbox
            v-if="data.representativeDocumentType?.toApprove && mode == 'edit'"
            v-model="data.isApproved"
            @update:modelValue="onIsApprovedClick" />
          <BaseTableCellCheck v-else :item="data.isApproved || false" />
        </BaseDetailsTableRow>
        <!-- Approval Information -->
        <BaseDetailsTableRow
          :name="$t('representative.approvalUser')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          {{ selectedUser?.name || '-' }}
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('representative.status')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseTableCellStatus
            :item="!data.isInvalid"
            :color="data.isInvalid ? 'red' : 'green'"
            :textTrue="$t('common.valid')"
            :textFalse="$t('common.invalid')"
            :showFalse="true" />
        </BaseDetailsTableRow>

        <!-- Additional Information -->
        <BaseDetailsTableRow
          :name="$t('representative.remarks')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.remarks"
            color="base"
            flat
            :disabled="mode == 'view'"
            :warning="getFieldSoftError('remarks')"
            show-error-as-tooltip />
        </BaseDetailsTableRow>

        <!-- System Information -->
        <BaseDetailsTableRow
          :name="$t('baseObject.isFromManager')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseTableCellCheck :item="data.isFromManager || false" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.lastUpdate')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          {{ isoDateToFullDate(data.lastUpdate) || '-' }}
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.createdDate')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          {{ isoDateToFullDate(data.createdDate) || '-' }}
        </BaseDetailsTableRow>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useLookupsStore } from '@/stores/lookups';
import { isoDateToFullDate } from '@/utils/date';
import { useI18n } from 'vue-i18n';

import BaseDetailsTableRow from '@/components/common/BaseDetailsTableRow.vue';
import BaseTableCellCheck from '@/components/common/BaseTableCellCheck.vue';
import BaseTableCellStatus from '@/components/common/BaseTableCellStatus.vue';
import BaseInput from '../common/BaseInput.vue';
import BaseCheckbox from '../common/BaseCheckbox.vue';

import type { DCDocumentHeader } from '@/types/DataCollection';
import type { SelectDropdownItem } from '@/types/Base';

const { t: $t } = useI18n();

const props = defineProps<{
  mode: 'view' | 'edit';
  isHorizontalLayout?: boolean;
  validations?: any;
}>();

// Helper to get field-level warning message
function getFieldSoftError(fieldName: string): string {
  if (!props.validations || props.mode === 'view') return '';

  const validationField = props.validations[fieldName];
  if (!validationField?.dynamicRequired) return '';

  return validationField.dynamicRequired.$invalid ? validationField.dynamicRequired.$message : '';
}

const data = defineModel<Partial<DCDocumentHeader> | null>();
const { getUsersLookup } = useLookupsStore();
const { dropdownUsers } = storeToRefs(useLookupsStore());

onMounted(async () => {
  if (!data.value) return;
  await getUsersLookup();
  initProperties();
});

function initProperties() {
  if (data.value?.approvalUserId) {
    const user = dropdownUsers.value.find((x) => x.value === data.value?.approvalUserId);

    if (user) selectedUser.value = user;
  }
}

const selectedUser = ref<SelectDropdownItem | null>(null);
function onIsApprovedClick(val: boolean | undefined) {
  if (!data.value) return;
  if (val) {
    const sessionDetailsString = localStorage.getItem('sessionDetails');
    if (!sessionDetailsString) return;
    const sessionDetails = JSON.parse(sessionDetailsString);
    const activeUser = dropdownUsers.value.find(
      (x) => x.value === sessionDetails.applicationUserID
    );
    if (activeUser) {
      selectedUser.value = activeUser;
      data.value.approvalUserId = activeUser.value.toString();
    }
  } else {
    selectedUser.value = null;
    data.value.approvalUserId = undefined;
  }
}
</script>
